import type {
  AdminRole,
  AdminUser,
  AffiliateNetwork,
  AffiliateOrder,
  AffiliateOrderImportResult,
  Brand,
  Category,
  Channel,
  ChannelAffiliateConfig,
  ChannelProduct,
  ChannelScraperConfig,
  CommissionSettlement,
  EntityStatus,
  FlashSale,
  OutboundClick,
  PriceSnapshot,
  ScraperRun,
  ScraperType,
  StandardProduct,
  StockStatus,
} from "@glow-price/shared";

import { apiRequest } from "./client";

export type ListResponse<T> = {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
};

export type ImportSummary = {
  totalRows: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: { row: number; reason: string }[];
};

export type ChannelStaleness = {
  channelId: string;
  channelName: string;
  cpCount: number;
  freshestCapturedAt: string | null;
  hoursStale: number | null;
};

export type AdminStats = {
  totals: {
    brands: number;
    products: number;
    channels: number;
    channelProducts: number;
    snapshots: number;
    users: number;
    favorites: number;
    outboundClicks: number;
  };
  today: {
    outboundClicks: number;
    favorites: number;
    snapshots: number;
  };
  clicks7d: { date: string; count: number }[];
  topChannels: { channelId: string; channelName: string; count: number }[];
  dataHealth: {
    productsWithoutImage: number;
    productsWithoutTags: number;
    productsWithoutChannel: number;
    productsWithStaleSnapshots: number;
    channelStaleness: ChannelStaleness[];
  };
};

// ─── stats ────────────────────────────────────────────────
export const statsApi = {
  get: () => apiRequest<AdminStats>("/api/admin/stats"),
};

// ─── home settings ────────────────────────────────────────
import type { HomeBanner, HomeFeature } from "@glow-price/shared";
export const homeSettingsApi = {
  getFeature: () => apiRequest<HomeFeature>("/api/admin/home-settings/feature"),
  putFeature: (body: HomeFeature) =>
    apiRequest<HomeFeature>("/api/admin/home-settings/feature", {
      method: "PUT",
      body,
    }),
  getBanners: () => apiRequest<{ items: HomeBanner[] }>("/api/admin/home-settings/banners"),
  putBanners: (items: HomeBanner[]) =>
    apiRequest<{ items: HomeBanner[] }>("/api/admin/home-settings/banners", {
      method: "PUT",
      body: { items },
    }),
};

// ─── uploads ──────────────────────────────────────────────
export const uploadsApi = {
  upload: (file: File, folder?: string) => {
    const fd = new FormData();
    fd.append("file", file);
    if (folder) fd.append("folder", folder);
    return apiRequest<{ key: string; url: string; size: number; contentType: string }>(
      "/api/admin/uploads",
      { method: "POST", formData: fd },
    );
  },
};

// ─── categories ────────────────────────────────────────────
export const categoriesApi = {
  list: () => apiRequest<{ items: Category[] }>("/api/admin/categories"),
  create: (body: Partial<Category>) =>
    apiRequest<Category>("/api/admin/categories", { method: "POST", body }),
  update: (id: string, body: Partial<Category>) =>
    apiRequest<Category>(`/api/admin/categories/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/categories/${id}`, { method: "DELETE" }),
};

// ─── brands ────────────────────────────────────────────────
export const brandsApi = {
  list: (params: { q?: string; page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<Brand>>("/api/admin/brands", { query: params }),
  get: (id: string) => apiRequest<Brand>(`/api/admin/brands/${id}`),
  create: (body: Partial<Brand>) =>
    apiRequest<Brand>("/api/admin/brands", { method: "POST", body }),
  update: (id: string, body: Partial<Brand>) =>
    apiRequest<Brand>(`/api/admin/brands/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/brands/${id}`, { method: "DELETE" }),
};

// ─── standard products ────────────────────────────────────────
export type AdminStandardProductRow = {
  product: StandardProduct;
  brand: Brand;
  lowestFinalPrice: number | null;
  channelCount: number;
};

export const standardProductsApi = {
  list: (params: { q?: string; brandId?: string; category?: string; tag?: string; page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<AdminStandardProductRow>>("/api/admin/standard-products", { query: params }),
  get: (id: string) => apiRequest<StandardProduct>(`/api/admin/standard-products/${id}`),
  create: (body: Partial<StandardProduct>) =>
    apiRequest<StandardProduct>("/api/admin/standard-products", { method: "POST", body }),
  update: (id: string, body: Partial<StandardProduct>) =>
    apiRequest<StandardProduct>(`/api/admin/standard-products/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/standard-products/${id}`, { method: "DELETE" }),
  importCsv: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiRequest<ImportSummary>("/api/admin/standard-products/import", {
      method: "POST",
      formData: fd,
    });
  },
};

// ─── channels ────────────────────────────────────────────────
export const channelsApi = {
  list: (params: { q?: string; page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<Channel>>("/api/admin/channels", { query: params }),
  get: (id: string) => apiRequest<Channel>(`/api/admin/channels/${id}`),
  create: (body: Partial<Channel>) =>
    apiRequest<Channel>("/api/admin/channels", { method: "POST", body }),
  update: (id: string, body: Partial<Channel>) =>
    apiRequest<Channel>(`/api/admin/channels/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/channels/${id}`, { method: "DELETE" }),
};

// ─── channel products ────────────────────────────────────────
export const channelProductsApi = {
  list: (params: { standardProductId?: string; channelId?: string; page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<ChannelProduct>>("/api/admin/channel-products", { query: params }),
  get: (id: string) => apiRequest<ChannelProduct>(`/api/admin/channel-products/${id}`),
  create: (body: Partial<ChannelProduct>) =>
    apiRequest<ChannelProduct>("/api/admin/channel-products", { method: "POST", body }),
  update: (id: string, body: Partial<ChannelProduct>) =>
    apiRequest<ChannelProduct>(`/api/admin/channel-products/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/channel-products/${id}`, { method: "DELETE" }),
};

// ─── price snapshots ─────────────────────────────────────────
export const priceSnapshotsApi = {
  list: (params: { channelProductId?: string; sinceCapturedAt?: string; page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<PriceSnapshot>>("/api/admin/price-snapshots", { query: params }),
  create: (body: Partial<PriceSnapshot>) =>
    apiRequest<PriceSnapshot>("/api/admin/price-snapshots", { method: "POST", body }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/price-snapshots/${id}`, { method: "DELETE" }),
  importCsv: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiRequest<ImportSummary>("/api/admin/price-snapshots/import", {
      method: "POST",
      formData: fd,
    });
  },
};

// ─── affiliate networks ───────────────────────────────────────
export const affiliateNetworksApi = {
  list: (params: { page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<AffiliateNetwork>>("/api/admin/affiliate-networks", { query: params }),
  create: (body: Partial<AffiliateNetwork>) =>
    apiRequest<AffiliateNetwork>("/api/admin/affiliate-networks", { method: "POST", body }),
  update: (id: string, body: Partial<AffiliateNetwork>) =>
    apiRequest<AffiliateNetwork>(`/api/admin/affiliate-networks/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/affiliate-networks/${id}`, { method: "DELETE" }),
};

// ─── channel affiliate configs ───────────────────────────────
export const channelAffiliateConfigsApi = {
  list: (params: { channelId?: string; networkId?: string; page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<ChannelAffiliateConfig>>("/api/admin/channel-affiliate-configs", { query: params }),
  create: (body: Partial<ChannelAffiliateConfig>) =>
    apiRequest<ChannelAffiliateConfig>("/api/admin/channel-affiliate-configs", { method: "POST", body }),
  update: (id: string, body: Partial<ChannelAffiliateConfig>) =>
    apiRequest<ChannelAffiliateConfig>(`/api/admin/channel-affiliate-configs/${id}`, {
      method: "PATCH",
      body,
    }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/channel-affiliate-configs/${id}`, { method: "DELETE" }),
};

// ─── outbound clicks ─────────────────────────────────────────
export const outboundClicksApi = {
  list: (params: { channelId?: string; status?: string; page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<OutboundClick>>("/api/admin/outbound-clicks", { query: params }),
};

// ─── affiliate orders ────────────────────────────────────────
export const affiliateOrdersApi = {
  list: (params: {
    networkId?: string;
    channelId?: string;
    clickId?: string;
    status?: string;
    page?: number;
    pageSize?: number;
  }) =>
    apiRequest<ListResponse<AffiliateOrder>>("/api/admin/affiliate-orders", { query: params }),
  importCsv: (file: File) => {
    const fd = new FormData();
    fd.append("file", file);
    return apiRequest<AffiliateOrderImportResult>("/api/admin/affiliate-orders/import", {
      method: "POST",
      formData: fd,
    });
  },
};

// ─── flash sales ─────────────────────────────────────────────
export const flashSalesApi = {
  list: (params: { status?: string; page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<FlashSale>>("/api/admin/flash-sales", { query: params }),
  create: (body: Partial<FlashSale>) =>
    apiRequest<FlashSale>("/api/admin/flash-sales", { method: "POST", body }),
  update: (id: string, body: Partial<FlashSale>) =>
    apiRequest<FlashSale>(`/api/admin/flash-sales/${id}`, { method: "PATCH", body }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/flash-sales/${id}`, { method: "DELETE" }),
};

// ─── commission settlements ──────────────────────────────────
export const commissionSettlementsApi = {
  list: (params: { networkId?: string; page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<CommissionSettlement>>("/api/admin/commission-settlements", {
      query: params,
    }),
};

// ─── legal documents ────────────────────────────────────────
import type { LegalDocument } from "@glow-price/shared";
export type LegalDocKey = "privacy" | "terms";
export const legalDocsApi = {
  get: (key: LegalDocKey) =>
    apiRequest<LegalDocument>(`/api/admin/legal/${key}`),
  put: (
    key: LegalDocKey,
    body: Pick<LegalDocument, "version" | "title" | "bodyMarkdown" | "requireReconsent">,
  ) => apiRequest<LegalDocument>(`/api/admin/legal/${key}`, { method: "PUT", body }),
};

// ─── channel scrapers ────────────────────────────────────────
// Re-export shared types so callers in the admin app keep the existing import
// path. Shared types include the Phase 6C fields (maxConcurrentFetches /
// retryOnFailure / consecutiveFailures / alertAfterFailures / pausedAt /
// pausedReason on ChannelScraperConfig; dryRun / priceChangesCount /
// durationMs on ScraperRun).
export type { ChannelScraperConfig, ScraperRun, ScraperType };

export type ChannelScraperRow = {
  channel: Channel;
  scraper: ChannelScraperConfig | null;
};

export type ChannelScraperUpdateBody = {
  scraperType: ScraperType;
  enabled: boolean;
  scheduleCron: string;
  configJson: Record<string, unknown>;
  /** Phase 6D-2: tunable orchestrator knobs. Optional so legacy update
   *  callers don't have to send them. */
  maxConcurrentFetches?: number;
  retryOnFailure?: boolean;
  alertAfterFailures?: number;
};

/**
 * Mirror of `ScrapedSnapshot` from apps/api/src/services/scrapers/types.ts.
 * Inline here because shared/ does not export it (it's an API-internal
 * intermediate, surfaced only via the dry-run response).
 */
export type ScrapedSnapshot = {
  channelProductId: string;
  listPrice: number | null;
  salePrice: number | null;
  couponPrice: number | null;
  finalPrice: number;
  stockStatus: StockStatus;
  capturedAt: string;
};

export type ScraperError = {
  channelProductId?: string;
  reason: string;
};

export type DryRunResult = {
  runId: string;
  status: ScraperRun["status"];
  inserted: number;
  skipped: number;
  priceChanges: number;
  durationMs: number | null;
  snapshots: ScrapedSnapshot[];
  snapshotCountTotal: number;
  errors: ScraperError[];
};

export type BatchAction = "enable" | "disable" | "pause" | "resume" | "reset-failures";

export const channelScrapersApi = {
  list: () =>
    apiRequest<{ items: ChannelScraperRow[] }>("/api/admin/channel-scrapers"),
  update: (channelId: string, body: ChannelScraperUpdateBody) =>
    apiRequest<ChannelScraperConfig>(`/api/admin/channel-scrapers/${channelId}`, {
      method: "PUT",
      body,
    }),
  runNow: (channelId: string) =>
    apiRequest<{ runId: string; status: ScraperRun["status"] }>(
      `/api/admin/channel-scrapers/${channelId}/run-now`,
      { method: "POST" },
    ),
  dryRun: (channelId: string) =>
    apiRequest<DryRunResult>(`/api/admin/channel-scrapers/${channelId}/dry-run`, {
      method: "POST",
    }),
  resume: (channelId: string) =>
    apiRequest<ChannelScraperConfig>(`/api/admin/channel-scrapers/${channelId}/resume`, {
      method: "PATCH",
    }),
  batch: (body: { ids: string[]; action: BatchAction; reason?: string }) =>
    apiRequest<{ changed: number; ids: string[]; action: BatchAction }>(
      "/api/admin/channel-scrapers/batch",
      { method: "PATCH", body },
    ),
  listRuns: (channelId: string, params: { page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<ScraperRun>>(
      `/api/admin/channel-scrapers/${channelId}/runs`,
      { query: params },
    ),
};

// ─── audit logs ──────────────────────────────────────────────
export type AuditLog = {
  id: string;
  adminUserId: string;
  adminUsername: string;
  action: string;
  entityType: string;
  entityId: string | null;
  beforeJson: string | null;
  afterJson: string | null;
  ip: string | null;
  userAgent: string | null;
  createdAt: string;
};

export const auditLogsApi = {
  list: (params: {
    adminUserId?: string;
    entityType?: string;
    entityId?: string;
    action?: string;
    page?: number;
    pageSize?: number;
  }) =>
    apiRequest<ListResponse<AuditLog>>("/api/admin/audit-logs", { query: params }),
};

// ─── site settings (Phase 6D-2) ──────────────────────────────
//
// Generic key/value store backing `home_feature`, `home_banners`, legal docs
// and ad-hoc operations toggles. The dedicated wrappers above (homeSettingsApi,
// legalDocsApi) still own their typed shapes — this is the escape hatch for
// the System Settings page (SuperAdmin / Editor only) and any new key that
// hasn't earned its own typed wrapper yet.
//
// TODO(api): the backend currently exposes only typed routes
// (/api/admin/home-settings/*, /api/admin/legal/*). A generic
// `/api/admin/site-settings?key=` (and PUT) does not exist yet. Until the
// API agent lands it, this admin page reads/writes the known keys via the
// existing typed endpoints. The thin wrapper below documents the intended
// shape for when the generic endpoint ships.
export type SiteSettingValue = unknown;

export const siteSettingsApi = {
  /**
   * Reads one site setting by key. NOT YET IMPLEMENTED on the backend —
   * the System Settings page uses the typed endpoints (home-settings,
   * legal-docs) directly. Kept here as the expected eventual shape so we
   * don't have to refactor callers when the API lands.
   */
  get: (key: string) =>
    apiRequest<{ key: string; value: SiteSettingValue }>(`/api/admin/site-settings/${key}`),
  /** Writes one site setting by key. See `get` above for status. */
  put: (key: string, value: SiteSettingValue) =>
    apiRequest<{ key: string; value: SiteSettingValue }>(`/api/admin/site-settings/${key}`, {
      method: "PUT",
      body: { value },
    }),
};

// ─── admin users (Phase 6D-1) ────────────────────────────────
//
// All mutations require SUPERADMIN role on the server side. The UI hides the
// controls via RoleGuard / useCurrentAdmin, but the wire is still the source
// of truth — these calls will 403 if the caller is not a superadmin.
export type AdminUserCreateInput = {
  username: string;
  email?: string | null;
  displayName?: string | null;
  role: AdminRole;
  password: string;
};

export type AdminUserUpdateInput = {
  email?: string | null;
  displayName?: string | null;
  role?: AdminRole;
  status?: EntityStatus;
};

export const adminUsersApi = {
  list: (params: { page?: number; pageSize?: number }) =>
    apiRequest<ListResponse<AdminUser>>("/api/admin/admin-users", { query: params }),
  get: (id: string) => apiRequest<AdminUser>(`/api/admin/admin-users/${id}`),
  create: (body: AdminUserCreateInput) =>
    apiRequest<AdminUser>("/api/admin/admin-users", { method: "POST", body }),
  update: (id: string, body: AdminUserUpdateInput) =>
    apiRequest<AdminUser>(`/api/admin/admin-users/${id}`, { method: "PATCH", body }),
  // NOTE: backend uses PATCH for these two — see apps/api admin-users.ts.
  // Spec/doc may say POST, but PATCH is the actual deployed contract.
  resetPassword: (id: string, newPassword: string) =>
    apiRequest<{ ok: true }>(`/api/admin/admin-users/${id}/reset-password`, {
      method: "PATCH",
      body: { newPassword },
    }),
  unlock: (id: string) =>
    apiRequest<{ ok: true }>(`/api/admin/admin-users/${id}/unlock`, { method: "PATCH" }),
  remove: (id: string) =>
    apiRequest<{ id: string }>(`/api/admin/admin-users/${id}`, { method: "DELETE" }),
};
